import { GetAccountsResponse } from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import { differenceInMonths } from 'date-fns';
import _ from 'lodash';

import { TProductType } from '../../../myAccount/dashboard/Dashboard.types';
import {
    canRequestTopUp,
    canTopUpAfterRepayments,
    hasAvailableCash,
    hasInterestOnlyExtensionApproval,
    isPreApproved,
    isTopUpApproved,
    isTopUpDeclined,
    isTopUpPending
} from '../accountsSelector';

type TAccount = NonNullable<GetAccountsResponse['data']>['accounts'][number];

export type TTopUpStatus =
    | 'NOT_YET_TOPUP_ELIGIBLE'
    | 'TOPUP_PENDING'
    | 'TOPUP_DECLINED'
    | 'TOPUP_ELIGIBLE'
    | 'TOPUP_APPROVED'
    | 'PRE_APPROVED'
    | 'INTEREST_ONLY_EXTENSION_APPROVED'
    | 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS'
    | undefined;

export const getTopUpStatus = (
    state: RootState,
    productType: TProductType
): TTopUpStatus => {
    const isNotYetTopUpEligible = canTopUpAfterRepayments(state, productType);
    const canTopUpNow = canRequestTopUp(state, productType);
    const topUpPending = isTopUpPending(state, productType);
    const topUpDeclined = isTopUpDeclined(state);
    const preApproved = isPreApproved(state, productType);
    const topUpApproved = isTopUpApproved(state, productType);

    let hasDeclineExpired;

    if (state.api.application) {
        const statusDate = state.api.application.data?.status_date;
        const declineStatus = state.api.application.data?.decline_status;

        const monthsSinceStatusChange = differenceInMonths(
            new Date(),
            statusDate
        );

        if (
            (declineStatus === 'Soft Declined' &&
                monthsSinceStatusChange >= 3) ||
            (declineStatus === 'Hard Declined' && monthsSinceStatusChange >= 6)
        ) {
            hasDeclineExpired = true;
        }
    }

    const interestOnlyExtensionApproved = hasInterestOnlyExtensionApproval(
        state,
        productType
    );

    if (preApproved) {
        if (topUpPending) {
            return 'TOPUP_PENDING';
        }

        if (topUpDeclined && !hasDeclineExpired) {
            return 'TOPUP_DECLINED';
        }

        return 'PRE_APPROVED';
    }

    if (interestOnlyExtensionApproved) {
        if (hasAvailableCash(state, productType)) {
            return 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS';
        }

        return 'INTEREST_ONLY_EXTENSION_APPROVED';
    }

    if (topUpApproved) return 'TOPUP_APPROVED';
    if (topUpPending) return 'TOPUP_PENDING';
    if (isNotYetTopUpEligible) return 'NOT_YET_TOPUP_ELIGIBLE';
    if (topUpDeclined && !hasDeclineExpired) return 'TOPUP_DECLINED';
    if (canTopUpNow) return 'TOPUP_ELIGIBLE';

    return undefined;
};

export const getMaxMonthlyInterestRate = (account: TAccount) => {
    const rates =
        account.scheduled_payments?.summary?.rate_structures![0].rates;
    const maxRate = _.maxBy(rates, 'max_principal');

    return maxRate!.rate;
};

export const repeatsTopUpEligibilitySplitTestName =
    'repeats_new_topup_eligibility_criteria_2025';
